import InputField from "components/Common/InputField"
import FormikCreatableSelect from "components/Common/FormikCreatableSelect"
import ThemeModal from "components/Common/ThemeModal"
import { useMemo, useCallback } from "react"
import { toast } from "react-toastify"
import { Form, FormFeedback, Label, Button } from "reactstrap"
import * as Yup from "yup"
import { useFormik } from "formik"
import {
  useCreateServiceList,
  useEditServiceList,
  useServiceLists,
} from "hooks/useServiceList"

const ServiceListAddEdit = ({ modal, toggleModal, isEdit, editedData, onSuccess }) => {
  const { mutateAsync: createMutation, isPending: createLoading } =
    useCreateServiceList()
  const { mutateAsync: editMutation, isPending: editLoading } =
    useEditServiceList()

  const { data: parentResponse, isLoading: parentLoading } = useServiceLists(
    { all: true, per_page: 200 },
    modal
  )
  const parents = parentResponse?.data?.data || parentResponse?.data || []

  const parentOptions = useMemo(
    () =>
      Array.isArray(parents)
        ? parents
            .filter(p => String(p.id) !== String(editedData?.id))
            .map(p => ({ value: p.id, label: p.name }))
        : [],
    [parents, editedData]
  )

  const validation = Yup.object({
    name: Yup.string().trim().required("Please enter service name"),
    description: Yup.string().nullable(),
    parent_id: Yup.mixed().nullable(),
  })

  const onSubmit = useCallback(
    async (values, { resetForm }) => {
      const payload = {
        name: values.name?.trim(),
        description: values.description?.trim() || null,
        parent_id: values.parent_id || null,
      }

      const action = isEdit
        ? editMutation({ id: editedData.id, ...payload })
        : createMutation(payload)

      try {
        const res = await action
        toast.success(
          res?.message ?? `Service list ${isEdit ? "updated" : "created"} successfully.`
        )
        resetForm()
        toggleModal()
        onSuccess?.()
      } catch (error) {
        const msg = error?.response?.data?.message || "Failed to save service list."
        toast.error(msg)
      }
    },
    [isEdit, editedData, editMutation, createMutation, toggleModal, onSuccess]
  )

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: editedData?.name || "",
      description: editedData?.description || "",
      parent_id: editedData?.parent_id || "",
    },
    validationSchema: validation,
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit,
  })

  const buttonText = `${
    isEdit ? "Save Changes" : "Add Service List"
  }${createLoading || editLoading ? "..." : ""}`

  return (
    <ThemeModal
      isOpen={modal}
      toggle={toggleModal}
      title={isEdit ? "Edit Service List" : "Add Service List"}
    >
      <Form
        onSubmit={e => {
          e.preventDefault()
          formik.handleSubmit()
        }}
      >
        <div className="mb-3">
          <Label htmlFor="name">Name</Label>
          <InputField
            type="text"
            name="name"
            id="name"
            value={formik.values.name}
            onChange={formik.handleChange}
            invalid={formik.touched.name && !!formik.errors.name}
          />
          {formik.touched.name && formik.errors.name && (
            <FormFeedback type="invalid">{formik.errors.name}</FormFeedback>
          )}
        </div>

        <div className="mb-3">
          <FormikCreatableSelect
            name="parent_id"
            label="Parent Service"
            formik={formik}
            options={parentOptions}
            loading={parentLoading}
            placeholder="Select parent (optional)"
          />
        </div>

        <div className="mb-3">
          <Label htmlFor="description">Description</Label>
          <InputField
            type="textarea"
            name="description"
            id="description"
            rows="3"
            value={formik.values.description}
            onChange={formik.handleChange}
          />
        </div>

        <div className="text-end">
          <Button
            color="success"
            type="submit"
            disabled={createLoading || editLoading}
          >
            {buttonText}
          </Button>
        </div>
      </Form>
    </ThemeModal>
  )
}

export default ServiceListAddEdit
